import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../queries/queryKeys';
import { useBroadcastListener } from '../../../services/broadcast/broadcastService';
import { collaborationService } from '../api/collaboration-service';

const unwrap = (response) => response?.data?.data ?? response?.data ?? null;
const MAX_CACHED_RADIUS_KM = 100;

const cachedDoctorFilters = (filters) => (
  filters?.mode === 'around'
    ? { ...filters, radius: MAX_CACHED_RADIUS_KM }
    : filters
);

const filterDoctorsByRadius = (data, filters) => {
  if (filters?.mode !== 'around') return data;

  const radiusKm = Number(filters.radius) || 25;
  return {
    ...data,
    items: data.items.filter((doctor) => Number(doctor.distanceKm) <= radiusKm),
    meta: { ...data.meta, radiusKm },
  };
};

const invalidate = (queryClient, collaborationId, patientId) => Promise.all([
  queryClient.invalidateQueries({ queryKey: queryKeys.collaborations.all }),
  collaborationId
    ? queryClient.invalidateQueries({ queryKey: queryKeys.collaborations.detail(collaborationId) })
    : Promise.resolve(),
  patientId
    ? queryClient.invalidateQueries({ queryKey: queryKeys.collaborations.activeForPatient(patientId) })
    : Promise.resolve(),
]);

export function useCollaborations(options = {}) {
  return useQuery({
    queryKey: queryKeys.collaborations.list(),
    queryFn: async ({ signal }) => unwrap(await collaborationService.list({ signal })),
    staleTime: 30_000,
    ...options,
  });
}

export function useCollaboration(collaborationId, options = {}) {
  return useQuery({
    queryKey: queryKeys.collaborations.detail(collaborationId),
    queryFn: async ({ signal }) => unwrap(await collaborationService.detail(collaborationId, { signal })),
    enabled: Boolean(collaborationId),
    ...options,
  });
}

export function useCollaborationNotes(collaborationId, options = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.collaborations.notes(collaborationId),
    queryFn: async ({ pageParam, signal }) => {
      const response = await collaborationService.notes(collaborationId, pageParam, { signal });
      return { items: unwrap(response) || [], meta: response?.data?.meta || {} };
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.meta.hasMore ? lastPage.meta.nextCursor : undefined,
    enabled: Boolean(collaborationId),
    ...options,
  });
}

export function useConsultationNote(collaborationId, appointmentId) {
  return useQuery({
    queryKey: queryKeys.collaborations.consultationNote(collaborationId, appointmentId),
    queryFn: async ({ signal }) => unwrap(await collaborationService.consultationNote(collaborationId, appointmentId, { signal })),
    enabled: Boolean(collaborationId && appointmentId),
    staleTime: 30_000,
  });
}

export function useSaveConsultationNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collaborationId, appointmentId, text }) => collaborationService.saveConsultationNote(collaborationId, appointmentId, text),
    onSuccess: (response, { collaborationId, appointmentId }) => {
      queryClient.setQueryData(queryKeys.collaborations.consultationNote(collaborationId, appointmentId), unwrap(response));
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.collaborations.notes(collaborationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.collaborations.list() }),
      ]);
    },
  });
}

export function useActiveCollaboration(patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.collaborations.activeForPatient(patientId),
    queryFn: async ({ signal }) => unwrap(await collaborationService.activeForPatient(patientId, { signal })),
    enabled: Boolean(patientId),
    staleTime: 30_000,
    ...options,
  });
}

export function useDoctorSearch(filters, options = {}) {
  const requestFilters = cachedDoctorFilters(filters);
  const { select, ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.collaborations.doctors(requestFilters),
    queryFn: async ({ signal }) => {
      const response = await collaborationService.searchDoctors(requestFilters, { signal });
      return { items: unwrap(response) || [], meta: response?.data?.meta || {} };
    },
    enabled: Boolean(filters),
    placeholderData: (previous) => previous,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    select: (data) => {
      const filtered = filterDoctorsByRadius(data, filters);
      return select ? select(filtered) : filtered;
    },
    ...queryOptions,
  });
}

export function usePatientSearch(query, options = {}) {
  return useQuery({
    queryKey: queryKeys.collaborations.patients(query),
    queryFn: async ({ signal }) => {
      const response = await collaborationService.searchPatients({ q: query }, { signal });
      return { items: unwrap(response) || [], meta: response?.data?.meta || {} };
    },
    enabled: String(query || '').trim().length >= 2,
    placeholderData: (previous) => previous,
    ...options,
  });
}

export function useCollaborationSpecialties(options = {}) {
  return useQuery({
    queryKey: queryKeys.collaborations.specialties(),
    queryFn: async ({ signal }) => unwrap(await collaborationService.specialties({ signal })) || [],
    staleTime: 30 * 60_000,
    ...options,
  });
}

function useCollaborationMutation(mutationFn) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (response, variables) => {
      const data = unwrap(response);
      return invalidate(
        queryClient,
        data?.id || variables?.collaborationId,
        data?.patient?.id || variables?.patientId,
      );
    },
  });
}

export const useCreateCollaboration = () => useCollaborationMutation(
  (values) => collaborationService.create(values),
);
export const useRespondToCollaboration = () => useCollaborationMutation(
  ({ collaborationId, decision }) => collaborationService.respond(collaborationId, decision),
);
export const useInviteCollaborationDoctor = () => useCollaborationMutation(
  ({ collaborationId, doctorId }) => collaborationService.inviteDoctor(collaborationId, doctorId),
);
export const useRemoveCollaborationDoctor = () => useCollaborationMutation(
  ({ collaborationId, doctorId }) => collaborationService.removeDoctor(collaborationId, doctorId),
);
export const useAddCollaborationNote = () => useCollaborationMutation(
  ({ collaborationId, ...values }) => collaborationService.addNote(collaborationId, values),
);
export const useLeaveCollaboration = () => useCollaborationMutation(
  ({ collaborationId }) => collaborationService.leave(collaborationId),
);
export const useArchiveCollaboration = () => useCollaborationMutation(
  ({ collaborationId }) => collaborationService.archive(collaborationId),
);

export function useCollaborationRealtime(userId) {
  const queryClient = useQueryClient();
  const onChanged = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.collaborations.all });
  }, [queryClient]);

  useBroadcastListener({
    channelName: userId ? `users.${userId}.collaboration` : '',
    isPrivate: true,
    events: { '.collaboration.changed': onChanged },
    enabled: Boolean(userId),
  });
}
